import { UploadStatus } from '../../../../utils/hooks/useFilesUploader/types';
import { MediaManagerSDKAnyUploadingFile } from '../../../../utils/media-manager-sdk';
import { useMemo } from 'react';
import {
  getUploadedSize,
  isFileInProgress,
  isFileInvalid,
  isFileProcessing,
  isFileUploaded,
  isFileWaiting,
  isUploadingSourceFile,
  getItemFromServerResponse,
} from '../../../../utils/uploadingFile';
import { isTranscodableMediaType, isTranscoding } from '../../../../utils/media-manager-sdk/transcoding';
import { contains } from 'ramda';
import { isPictureType } from '../../../../utils/media-manager-sdk/items';
import { getReadableFileSize } from '../../../../utils/files';
import { useIntl } from 'react-intl';

export const useUploadingFileProps = (uploadingFile: MediaManagerSDKAnyUploadingFile) => {
  const { formatNumber } = useIntl();

  return useMemo(() => {
    const {
      status,
      source: { file, mediaType },
    } = uploadingFile;
    const isPending = isFileWaiting(uploadingFile);
    const isInProgress = isFileInProgress(uploadingFile);
    const isProcessing = isFileProcessing(uploadingFile);
    const canBeAborted = isPending || isInProgress;
    const isCanceled = uploadingFile.isAborted();
    const uploadError = isFileInvalid(uploadingFile) && uploadingFile.error;
    const fileSize = uploadingFile.getFileSize();

    // imports from outer sources may not have size
    const uploadProgressPercent = fileSize ? getUploadedSize(uploadingFile) / fileSize : null;

    const destinationFolderName = uploadingFile.options.folderName;
    const isInvalid = Boolean(uploadError) && !isCanceled;
    const isUploaded = isFileUploaded(uploadingFile);
    const isAddTagsButtonDisabled =
      (isTranscodableMediaType(mediaType) &&
        contains(status, [UploadStatus.Waiting, UploadStatus.Uploading, UploadStatus.Processing])) ||
      (isUploaded && isTranscoding(getItemFromServerResponse(uploadingFile)));
    const isPicture = isPictureType(uploadingFile.source.mediaType);
    const isFailed = isCanceled || isInvalid;
    const isTagsSectionVisible = isUploadingSourceFile(file) && (isUploaded || (!isFailed && !isPicture));

    return {
      isPending,
      isInProgress,
      canBeAborted,
      isCanceled,
      isProcessing,
      isInvalid,
      isUploaded,
      uploadError,
      uploadProgressPercent,
      destinationFolderName,
      isAddTagsButtonDisabled,
      isPicture,
      isFailed,
      isTagsSectionVisible,
      mediaType: uploadingFile.source.mediaType,
      canRetry: uploadingFile.canRetry(),
      canResume: uploadingFile.canResume(),
      // imports from outer sources may not have size
      readableFileSize: fileSize ? getReadableFileSize(fileSize, formatNumber) : null,

      cancelFileUploading: () => uploadingFile.abort(),
      retryToUpload: () => uploadingFile.signUpForRetry(),
      resumeUpload: () => uploadingFile.signUpForResume(),
    };
  }, [uploadingFile]);
};
